import { computed, observable, when, action, makeObservable } from 'mobx';
import { bound } from '../../utils/decorators/basic';
import { runtime } from '../runtime/Runtime';
import { UnPromisify } from '../../pagepart/portal/utils';
import { str_replace } from '../../utils/helper/helper_string';
import { memoize } from '../../utils/mobx/decorators/memoize';

export class APIData<F extends (req, res) => Promise<any>> {
  readonly route: string;

  @observable.ref refObservablePayload;
  @observable.shallow shallowObservablePayload;

  @observable.ref lastFetchEnd: Date | undefined;
  isFetchingPromise: ReturnType<F>;

  readonly pollInterval: number;
  readonly defaultPayload;

  constructor({ route, defaultValue = undefined, pollInterval }: { route: string; defaultValue?: any; pollInterval?: number }) {
    makeObservable(this);
    this.defaultPayload = defaultValue;
    this.route = route;
    if (typeof window === 'undefined') {
      this.route = `https://${runtime.runtimeHostname.subdomain}.schlaukopf.de${this.route}`;
    } else {
      if (runtime.runtimePermissions.hasEditFunctionality) {
        this.route = str_replace('/api/', '/api/nocache/', this.route);
      } else {
        if (window.preload[route]) {
          const payload = window.preload[route].payload;
          if (payload) {
            this.refObservablePayload = payload;
            this.lastFetchEnd = new Date();
            this.isFetchingPromise = payload;
          }
        }
      }
    }
    this.pollInterval = pollInterval;
  }

  hasAccessedDeepPayload: boolean = false;

  @memoize
  get deepObservablePayload() {
    return observable.box(this.refObservablePayload, { deep: true });
  }

  @bound
  async refetch() {
    if (this.isFetchingPromise) await this.isFetchingPromise;
    await this.fetchValue();
  }

  @computed
  get isAvailable(): boolean {
    return typeof this.lastFetchEnd !== 'undefined';
  }

  get triggeredIsAvailable(): boolean {
    if (!this.lastFetchEnd && !this.isFetchingPromise) {
      setTimeout(() => this.fetchValue(), 1);
    }
    return this.isAvailable;
  }

  @computed
  get streamedRef(): UnPromisify<ReturnType<F>> {
    this.fetchIfUnavailable();
    return this.refObservablePayload || this.defaultPayload;
  }

  @computed
  get streamedDeep(): UnPromisify<ReturnType<F>> {
    this.hasAccessedDeepPayload = true;
    this.fetchIfUnavailable();
    return this.deepObservablePayload.get() || this.defaultPayload;
  }

  fetchIfUnavailable() {
    if (!this.lastFetchEnd) {
      setTimeout(() => this.fetchValue(), 1);
    }
  }

  @action.bound
  async fetchValue() {
    if (this.isFetchingPromise) {
      return await this.isFetchingPromise;
    }

    if (this.pollInterval) {
      setTimeout(this.fetchValue, this.pollInterval);
    }
    this.isFetchingPromise = this.provideFetchPromise() as any;
    const response: any = await this.isFetchingPromise;

    return response.data.payload;
  }

  @bound
  async provideFetchPromise() {
    const response = await runtime.axios.get(this.route, { headers: { Accept: '*/*' } }); // Accept header must match <link rel="preload" otherwise chrome won't use preload

    await runtime.runtimeScheduler.addToNextStateChange(() => {
      this.setPayload(response.data.payload);
    });
    return response;
  }
  // latestResponse;

  @action.bound
  setPayload(payload) {
    this.refObservablePayload = payload;
    if (this.hasAccessedDeepPayload) this.deepObservablePayload.set(payload);
    this.isFetchingPromise = undefined;
    this.lastFetchEnd = new Date();
    // if (this.shallowObservablePayload) this.shallowObservablePayload = payload;
  }
}
